import { supabase } from '../supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { Shift } from '@shiftly/shared';
import { DateTime } from 'luxon';
import { convertDateFields } from './supabaseShiftService';
import { useAuth } from '../../../contexts/AuthContext';
import { Permissions } from '@shiftly/shared/src/permissions';

export interface RealtimeShiftManagerOptions {
  enabled?: boolean;
  onError?: (error: any) => void;
}

export const useRealtimeShiftManager = (options: RealtimeShiftManagerOptions = {}) => {
  const { enabled = true, onError } = options;
  const { user, permissions, session, loading } = useAuth();
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);

  // Only enable if auth is ready and user is authenticated
  const shouldEnable = enabled && !loading && !!user && !!session && permissions.includes(Permissions.SHIFTS_VIEW);

  const updateShiftQueries = useCallback((payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    // Get all shift-related queries
    const shiftQueries = queryClient.getQueriesData<Shift[]>({
      queryKey: ['supabase-shifts']
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Supabase realtime update:', { eventType, recordId: newRecord?.id || oldRecord?.id });
    }

    shiftQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: Shift[] | undefined) => {
        if (!currentData) return currentData;

        switch (eventType) {
          case 'INSERT': {
            const shift = convertDateFields(newRecord);
            // Check if this shift should be included in this query
            if (shouldIncludeShiftInQuery(shift, queryKey)) {
              const exists = currentData.some(existingShift => existingShift.id === shift.id);
              if (!exists) {
                return [...currentData, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            }
            return currentData;
          }

          case 'UPDATE': {
            const shift = convertDateFields(newRecord);
            if (shouldIncludeShiftInQuery(shift, queryKey)) {
              // Update existing or add new
              const existingIndex = currentData.findIndex(existingShift => existingShift.id === shift.id);
              if (existingIndex >= 0) {
                const newData = [...currentData];
                newData[existingIndex] = shift;
                return newData.sort((a, b) => a.start_date_time.toMillis() - b.start_date_time.toMillis());
              } else {
                return [...currentData, shift].sort((a, b) =>
                  a.start_date_time.toMillis() - b.start_date_time.toMillis()
                );
              }
            } else {
              // Remove if no longer matches query criteria
              return currentData.filter(existingShift => existingShift.id !== shift.id);
            }
          }

          case 'DELETE': {
            return currentData.filter(shift => shift.id !== oldRecord.id);
          }

          default:
            return currentData;
        }
      });
    });

    // Also invalidate related queries like user stats
    if (newRecord?.user_id || oldRecord?.user_id) {
      const user_id = newRecord?.user_id || oldRecord?.user_id;
      queryClient.invalidateQueries({
        queryKey: ['supabase-user-period-stats', user_id],
        refetchType: 'none'
      });
    }
  }, [queryClient]);

  const shouldIncludeShiftInQuery = (shift: Shift, queryKey: readonly unknown[]): boolean => {
    // Extract query parameters from the query key
    const queryParams = queryKey[1] as any;
    if (!queryParams?.filters) return true;

    // Check if shift matches all filters
    return queryParams.filters.every((filter: any) => {
      const { field, operator, value } = filter;
      const shiftValue = getNestedValue(shift, field);

      switch (operator) {
        case 'eq':
          return shiftValue === value;
        case 'neq':
          return shiftValue !== value;
        case 'gt':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() > compareValue.toMillis();
          }
          return shiftValue > value;
        case 'gte':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() >= compareValue.toMillis();
          }
          return shiftValue >= value;
        case 'lt':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() < compareValue.toMillis();
          }
          return shiftValue < value;
        case 'lte':
          if (DateTime.isDateTime(shiftValue)) {
            const compareValue = DateTime.isDateTime(value) ? value : DateTime.fromISO(value);
            return shiftValue.toMillis() <= compareValue.toMillis();
          }
          return shiftValue <= value;
        case 'in':
          return Array.isArray(value) && value.includes(shiftValue);
        case 'not-in':
          return Array.isArray(value) && !value.includes(shiftValue);
        default:
          return true;
      }
    });
  };

  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  useEffect(() => {
    if (!shouldEnable) return;

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
        subscriptionRef.current = supabase
          .channel('realtime shift subscription', { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'shifts',
            },
            (payload) => {
              updateShiftQueries(payload);
            }
          )
          .subscribe((status, err) => {
            console.log('Realtime shift subscription status:', status);
            if (err && onError) {
              onError(err);
            }
          });
      } else {
        console.log('User signed out, cleaning up realtime shift subscription');
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
      }
    });

    return () => {
      console.log('Cleaning up realtime shift subscription');
      listener?.subscription.unsubscribe();
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [shouldEnable, updateShiftQueries, onError]);

  return {
    isConnected: !!subscriptionRef.current
  };
};